import { CloseIcon, EditIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Link,
  Spinner,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import {
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGetLoggedInUser } from '@revelio/auth';
import type { QuestionAnswer } from '@revelio/core';
import {
  BuildADashTrackingEvents,
  FeatureFlag,
  PagePaths,
} from '@revelio/core';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import {
  useUnleashFlag,
  useUnleashFlags,
} from '../../shared/unleash-flags/use-unleash-flag';
import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import {
  buildLlmTabContentFromPositionedItemForGlobalAssistant,
  getValidatedPositionedItemsFromLlmContent,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import { trackAIAssistantPrompt } from '../CustomDash/components/AIAssistant/track-ai-assistant-prompt';
import { usePageManager } from '../CustomDash/components/PageManagerProvider/PageManagerProvider';
import {
  GLOBAL_AI_OVERLAY_BOTTOM,
  GLOBAL_AI_OVERLAY_HEIGHT,
  GLOBAL_AI_OVERLAY_RIGHT,
  GLOBAL_AI_OVERLAY_WIDTH,
  GLOBAL_AI_OVERLAY_Z_INDEX,
} from '../CustomDash/constants/panelWidths';
import SpeechInput, {
  type SpeechInputHandle,
} from '../CustomDash/filters/components/filterForms/talent-discovery/ai-search/SpeechInput';
import { useReportBuilderConfig } from '../reports/hooks/useReportBuilderConfig';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../reports/utils/report-builder-paths';
import { getPromotedTemplateForReportOfferClick } from '../reports/utils/report-template-promotions';
import { formatClarificationQuestionsAsText } from './format-clarification-questions';
import {
  type GlobalAssistantAccessDecision,
  getGlobalAssistantCustomDashAccess,
  getGlobalAssistantRouteAccess,
} from './global-ai-assistant-access';
import {
  type CustomDashAssistantEntityType,
  type GlobalAssistantContextPolicy,
  type GlobalAssistantDebugTiming,
  type GlobalAssistantEntityType,
  type GlobalAssistantSuggestMatch,
  isCustomDashAssistantEntityType,
  postGlobalAssistantSingleChart,
  postGlobalAssistantSuggest,
  postGlobalAssistantTextChart,
  postGlobalAssistantWarmUp,
  toCustomDashAssistantEntityType,
} from './global-ai-assistant-api';
import { GlobalAIAssistantChartPreviewSlot } from './global-ai-assistant-chart-preview-slot';
import {
  COMMAND_TTL_MS,
  useGlobalAiCommandStore,
  waitForCommandComplete,
  waitForTaxonomyChange,
} from './global-ai-assistant-command-store';
import { useGlobalAIAssistant } from './global-ai-assistant-context';
import {
  applyConversationPolicy,
  resolveFollowOnContextPolicy,
  toConversationPayload,
} from './global-ai-assistant-context-policy';
import { buildBoundedConversationContext } from './global-ai-assistant-conversation';
import { shouldAutoRunCurrentCustomPageUpdate } from './global-ai-assistant-custom-page-update';
import {
  addCurrentPageFilterApplyOptionForDiscoveryMatch,
  buildGlobalAssistantCurrentPageContext,
  filterOutSelfLinkNavigationMatches,
  getAlreadyOnPageMessage,
  getDiscoveryApplyPrompt,
  hasCurrentPageFilterApplyOption,
  isCurrentPageFilterApplyMatch,
  isSelfLinkNavigationMatch,
  shouldAutoApplyDashboardFilterMatch,
  shouldAutoApplyDiscoveryMatch,
} from './global-ai-assistant-discovery-followup';
import { isGlobalAssistantFilterApplyCapablePath } from './global-ai-assistant-filter-apply-capability';
import {
  confirmGlobalAiAssistantRedirect,
  getGlobalAiAssistantRoutePathname,
} from './global-ai-assistant-navigation-guard';
import {
  getGlobalAssistantPageMatchAction,
  getGlobalAssistantPageMatchesDefaultMessage,
} from './global-ai-assistant-page-match-routing';
import { GlobalAssistantPermissionUpsell } from './global-ai-assistant-permission-upsell';
import { getGlobalAssistantReportAccess } from './global-ai-assistant-report-access';
import { launchReportFromOffer } from './global-ai-assistant-report-launch';
import {
  type ShouldShowReportOfferContext,
  shouldShowReportOffer,
} from './global-ai-assistant-report-offer-gating';
import { getGlobalAssistantRouteFilterKind } from './global-ai-assistant-route-filter-kind';
import {
  getRouteFilterResponse,
  prefetchRouteFilters,
} from './global-ai-assistant-route-filter-prefetch';
import {
  getSingleChartStandardPageButtonLabel,
  getSingleChartStandardPageMatch,
} from './global-ai-assistant-standard-page-match';
import { buildPromptForSubmit } from './global-ai-assistant-submit-prompt';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import { requestGlobalAssistantWarmUpOnce } from './global-ai-assistant-warm-up';
import {
  globalAssistantPathsMatch,
  inferEntityTypeFromNavigatePath,
} from './global-assistant-route-filter-path';
import { useGlobalAssistantTaxonomy } from './use-global-ai-assistant-taxonomy';

// Append-only: the drawer keeps the existing prompt when voice input starts
// (unlike the AiSearchFilterForm, which clears it).
const noop = () => {
  // intentionally empty
};

const BRAND_NAVY = '#2D426A';
const ASSISTANT_ICON_SIZE = '21.56px';

const OVERLAY_FONT_STACK = `'Source Sans Pro', system-ui, sans-serif`;

const CHIP_BUTTON_PROPS = {
  variant: 'outline' as const,
  borderColor: BRAND_NAVY,
  borderWidth: '0.83px',
  borderRadius: '4.15px',
  color: BRAND_NAVY,
  fontWeight: 600,
  fontSize: '10px',
  lineHeight: '13px',
  height: 'auto',
  minH: '25px',
  py: '6px',
  px: '8px',
  whiteSpace: 'normal' as const,
};

const SCOPE_REPLY_RE = /^(company|companies|geography|geographies|role|roles)$/;

function detectScopeReplyEntityType(
  reply: string
): Exclude<GlobalAssistantEntityType, 'talentDiscovery'> | undefined {
  const normalized = reply
    .trim()
    .toLowerCase()
    .replace(/[.?!,]+$/, '');
  if (!SCOPE_REPLY_RE.test(normalized)) {
    return undefined;
  }
  if (normalized.startsWith('compan')) {
    return 'company';
  }
  if (normalized.startsWith('geograph')) {
    return 'geography';
  }
  if (normalized.startsWith('role')) {
    return 'role';
  }
  return undefined;
}

function findLatestClarification(
  transcript: GlobalAiTranscriptMessage[]
): Extract<GlobalAiTranscriptMessage, { kind: 'clarification' }> | undefined {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const m = transcript[i];
    if (m && m.role === 'assistant' && m.kind === 'clarification') {
      return m;
    }
    if (m && m.role === 'user') {
      return undefined;
    }
  }
  return undefined;
}

function countUserTurns(transcript: GlobalAiTranscriptMessage[]): number {
  return transcript.reduce((count, message) => {
    return message.role === 'user' ? count + 1 : count;
  }, 0);
}

function addBackendTimingEvents(
  addPipelineEvent: (event: string) => void,
  timings?: GlobalAssistantDebugTiming[]
) {
  for (const timing of timings ?? []) {
    const modelSuffix = timing.model ? ` (${timing.model})` : '';
    addPipelineEvent(`backend ${timing.label}: ${timing.ms}ms${modelSuffix}`);
  }
}

function AssistantStarIcon() {
  return (
    <Box
      as="svg"
      width="14px"
      height="16px"
      viewBox="0 0 14 16"
      fill="none"
      aria-hidden
    >
      <path
        d="M7 0L8.5 5.5L14 7L8.5 8.5L7 16L5.5 8.5L0 7L5.5 5.5L7 0Z"
        fill="#FFFFFF"
      />
    </Box>
  );
}

function AssistantAvatar() {
  return (
    <Flex
      flexShrink={0}
      w={ASSISTANT_ICON_SIZE}
      h={ASSISTANT_ICON_SIZE}
      borderRadius="full"
      align="center"
      justify="center"
      bgGradient="linear(138deg, #1C81DF 5%, #1ED9D9 44%, #94E776 96%)"
      aria-hidden
    >
      <AssistantStarIcon />
    </Flex>
  );
}

function AssistantAvatarSpacer() {
  return (
    <Box
      flexShrink={0}
      w={ASSISTANT_ICON_SIZE}
      h={ASSISTANT_ICON_SIZE}
      aria-hidden
    />
  );
}

function AssistantAvatarSlot({ show }: { show: boolean }) {
  return show ? <AssistantAvatar /> : <AssistantAvatarSpacer />;
}

// True when the message renders the gradient assistant avatar at the start
// of its bubble. Used to collapse the avatar on consecutive assistant
// messages so the icon shows once per "run" instead of next to each bubble.
function messageRendersAvatar(msg: GlobalAiTranscriptMessage): boolean {
  if (msg.role !== 'assistant') {
    return false;
  }
  if (msg.kind === 'text' || msg.kind === 'clarification') {
    return true;
  }
  if (msg.kind === 'single_chart') {
    if (msg.item.plot?.chartType === 'text') {
      return true;
    }
    return Boolean(msg.intentSummary);
  }
  return false;
}

function normalizeAndNavigateToPath(
  targetPath: string,
  navigate: ReturnType<typeof useNavigate>,
  skipGuard: boolean
): string {
  const normalizedPath = getGlobalAiAssistantRoutePathname(targetPath);
  if (normalizedPath === targetPath) {
    return normalizedPath;
  }
  navigate(
    normalizedPath,
    skipGuard
      ? { replace: true, state: { skipGuard: true } }
      : { replace: true }
  );
  return normalizedPath;
}

export function GlobalAIAssistantDrawer() {
  const {
    isDrawerOpen,
    closeDrawer,
    draftPromptRef,
    hasRequestedWarmUp,
    markWarmUpRequested,
    lastUserPrompt,
    setLastUserPrompt,
    setGenerationStatus,
    generationStatus,
    transcript,
    appendTranscript,
    clearTranscript,
    sessionEntityScope,
    setSessionEntityScope,
    customDashAssistContextRef,
    addPipelineTimelineEvent,
    conversationSummary,
    conversationTrackingIdRef,
  } = useGlobalAIAssistant();

  const [draftPrompt, setDraftPromptLocal] = useState(
    () => draftPromptRef.current
  );
  const setDraftPrompt = useCallback(
    (value: SetStateAction<string>) => {
      setDraftPromptLocal((previous) => {
        const next = typeof value === 'function' ? value(previous) : value;
        draftPromptRef.current = next;
        return next;
      });
    },
    [draftPromptRef]
  );

  // Command store: drawer emits, bridge (inside CustomDash) handles.
  // Subscribe to the two slices separately so changes to in-flight state
  // don't re-render the drawer when emit identity hasn't changed.
  const emit = useGlobalAiCommandStore((state) => state.emit);
  // Per-entity taxonomy resolver. Primary source: the currently-mounted
  // bridge publishes its live `activeFilterMap`-derived taxonomy into the
  // store, so we pick up user session overrides (e.g. Preferences modal
  // selections not yet "Set as default"). Fallback: user's saved
  // preferences per scope, for cases where no bridge is active
  // (drawer opened on a non-CustomDash page, or entity mismatch).
  //
  // TODO(backend-adaptive): simplify to just `prefsTaxonomyByEntity` when
  // the backend accepts "adaptive". The store-publish path was added
  // primarily to detect adaptive re-resolution for the refinement pass in
  // `navigateToMatch`; once that's gone, the remaining value is niche
  // session-override support. See the refinement-pass docblock for the
  // full picture.
  const prefsTaxonomyByEntity = useGlobalAssistantTaxonomy();
  const activeTaxonomyByEntity = useGlobalAiCommandStore(
    (state) => state.activeTaxonomyByEntity
  );
  const lastAppliedFilterContextByPath = useGlobalAiCommandStore(
    (state) => state.lastAppliedFilterContextByPath
  );
  const taxonomyByEntity = useCallback(
    (entityType: GlobalAssistantEntityType) => {
      const prefs = prefsTaxonomyByEntity(entityType);
      const active = activeTaxonomyByEntity[entityType];
      if (!active) return prefs;
      // Bridge-published values win for the API payload (they reflect the
      // current page's resolved taxonomy), but cache-key identity always
      // comes from saved prefs — `rolesKey`/`skillsKey` are what keep the
      // cache from accumulating an entry per adaptive re-resolution.
      return {
        rolesTax: active.rolesTax,
        skillsTax: active.skillsTax,
        rolesKey: prefs.rolesKey,
        skillsKey: prefs.skillsKey,
      };
    },
    [activeTaxonomyByEntity, prefsTaxonomyByEntity]
  );
  const isCommandInFlight = useGlobalAiCommandStore(
    (state) => state.isCommandInFlight
  );

  const navigate = useNavigate();
  const location = useLocation();
  const { createPageDraft } = usePageManager();
  const { loggedInUser } = useGetLoggedInUser();
  const customDashNavEnabled = useUnleashFlag(
    FeatureFlag.CustomDashViewEditToggle
  );
  const userTabs = useMemo(() => loggedInUser.tabs ?? [], [loggedInUser.tabs]);
  const customDashAccess = useMemo(
    () => getGlobalAssistantCustomDashAccess(customDashNavEnabled),
    [customDashNavEnabled]
  );
  const customReportsIsEnabled = useUnleashFlag(FeatureFlag.CustomReports);
  const allFlags = useUnleashFlags();
  const { config: reportBuilderConfig } = useReportBuilderConfig();

  const [loading, setLoading] = useState(false);
  const [singleChartLoading, setSingleChartLoading] = useState(false);
  const [reportOfferLaunchingMessageId, setReportOfferLaunchingMessageId] =
    useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isReportOfferLaunching = reportOfferLaunchingMessageId !== null;
  const toast = useToast();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const speechInputRef = useRef<SpeechInputHandle>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Mirror of generationStatus so async callbacks (e.g. the safety
  // timeout in navigateToMatch) can read the latest value without
  // capturing a stale closure.
  const generationStatusRef = useRef<string | null>(generationStatus);
  generationStatusRef.current = generationStatus;
  const [transcriptScrollRoot, setTranscriptScrollRoot] =
    useState<HTMLDivElement | null>(null);
  const handleTranscriptScrollRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      setTranscriptScrollRoot((previousNode) => {
        return previousNode === node ? previousNode : node;
      });
    },
    []
  );

  const pathEntity = useMemo(
    () => inferEntityTypeFromNavigatePath(location.pathname, 'company'),
    [location.pathname]
  );

  const confirmAssistantRedirect = (targetPath?: string) => {
    return confirmGlobalAiAssistantRedirect({
      customDashContext: customDashAssistContextRef.current,
      currentPathname: location.pathname,
      targetPath,
    });
  };

  const onCloseAndStopRecording = useCallback(() => {
    speechInputRef.current?.stopRecording();
    closeDrawer();
  }, [closeDrawer]);

  useEffect(() => {
    return () => {
      speechInputRef.current?.stopRecording();
    };
  }, []);

  useEffect(() => {
    requestGlobalAssistantWarmUpOnce({
      isDrawerOpen,
      hasRequested: hasRequestedWarmUp,
      markRequested: markWarmUpRequested,
      onWarmUpError: (error) => {
        console.debug('Global assistant warm-up failed', error);
      },
      onWarmUpResult: (response) => {
        console.debug('Global assistant warm-up response', response);
      },
      requestWarmUp: postGlobalAssistantWarmUp,
    });
  }, [isDrawerOpen, hasRequestedWarmUp, markWarmUpRequested]);

  useEffect(() => {
    if (!isDrawerOpen) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseAndStopRecording();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen, onCloseAndStopRecording]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth',
    });
  }, [
    transcript.length,
    loading,
    singleChartLoading,
    generationStatus,
    isCommandInFlight,
    reportOfferLaunchingMessageId,
  ]);

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDraftPrompt(value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (textareaRef.current && isDrawerOpen) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [draftPrompt, isDrawerOpen]);

  const handleNewSession = () => {
    clearTranscript();
    conversationTrackingIdRef.current = crypto.randomUUID();
    setError(null);
    setDraftPrompt('');
    setLastUserPrompt(null);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const queueRouteFilterApply = useCallback(
    (args: {
      path: string;
      promptForFilters: string;
      entityType: GlobalAssistantEntityType;
      requestId?: string;
    }) => {
      const { path, promptForFilters, entityType } = args;
      const requestId = args.requestId ?? crypto.randomUUID();
      const filterKind = getGlobalAssistantRouteFilterKind(path);
      mixpanel.track(
        BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_QUEUED,
        {
          entity_type: entityType,
          global_assistant_route_filter_request_id: requestId,
        }
      );
      const RESOLVING_STATUS = 'Resolving filters…';
      setGenerationStatus(RESOLVING_STATUS);
      // Safety net: if no bridge ever claims this command (e.g. user
      // navigates elsewhere, or the target page has an incompatible
      // entity type), the queue entry expires at COMMAND_TTL_MS and the
      // "Resolving filters…" status would otherwise stay forever. Clear
      // it once the queue TTL has passed, but only if the status we set
      // is still the one on screen — a bridge may have legitimately
      // moved it to "Applying filters…" or an error, and we don't want
      // to wipe those.
      const clearIfStillResolving = setTimeout(() => {
        if (generationStatusRef.current === RESOLVING_STATUS) {
          setGenerationStatus(null);
        }
      }, COMMAND_TTL_MS);
      // Use whatever taxonomy is available right now — the store's
      // published value if any, or saved user prefs. For company-entity
      // + adaptive this may be wrong (adaptive resolution depends on the
      // target page's company filter, which the AI is about to apply).
      // The refinement pass below catches that case.
      const initialTaxonomy = taxonomyByEntity(entityType);
      void getRouteFilterResponse(promptForFilters, initialTaxonomy, {
        filterKind,
        screenerEntityType: toCustomDashAssistantEntityType(entityType),
      })
        .then((resolved) => {
          emit({
            kind: 'applyRouteFilters',
            requestId,
            ...resolved,
            targetPath: path,
            entityType,
            prompt: promptForFilters,
          });
          // Screener uses a different endpoint; do not run TD-only refinement.
          if (filterKind !== 'tdV2') return;
          // TODO(backend-adaptive): DELETE this entire refinement block
          // once the AI filter search endpoint accepts "adaptive" as a
          // taxonomy value.
          //
          // Why this exists today:
          // On company-entity pages with an adaptive role/skill taxonomy
          // preference, the "correct" taxonomy is a function of which
          // companies are in the filter map. But the AI response ITSELF
          // is what dispatches those companies — chicken-and-egg. So the
          // initial request above uses an approximate taxonomy (the
          // bridge's first-pass publish, which pre-dates the AI's
          // company filter), the bridge applies it, `useAdaptiveRoleTaxonomy`
          // / `useAdaptiveSkillTaxonomy` re-resolve with the new company
          // in scope, the bridge republishes with the corrected value,
          // and THIS block fires a second request+apply to refine.
          //
          // Why the future work eliminates it:
          // If the backend accepts `roles_tax: 'adaptive'` (and
          // `skills_tax: 'adaptive'` or equivalent), the drawer can send
          // "adaptive" directly, the backend computes the company-based
          // RICS resolution server-side using the same prompt-derived
          // company intent, and the response is correct on the first try.
          //
          // Other deletable pieces tagged with TODO(backend-adaptive):
          //   - `waitForCommandComplete` + `waitForTaxonomyChange` helpers
          //     in `global-ai-assistant-command-store.ts` (only this block
          //     uses them)
          //   - Bridge's `isRoleTaxonomyResolved`/`isSkillTaxonomyResolved`
          //     gate in `global-ai-assistant-bridge.tsx`
          //   - Store's `activeTaxonomyByEntity` + `publishActiveTaxonomy`
          //     (and the whole bridge publish-effect that feeds them) —
          //     re-evaluate; may still be useful for non-adaptive session
          //     overrides from the Preferences modal
          //   - Drawer's `activeTaxonomyByEntity` merge inside
          //     `taxonomyByEntity` — falls back to saved prefs only
          if (entityType !== 'company' && entityType !== 'talentDiscovery') {
            return;
          }
          void (async () => {
            const COMMAND_COMPLETE_WAIT_MS = 10_000;
            const TAXONOMY_CHANGE_WAIT_MS = 3_000;
            const initialCompleted = await waitForCommandComplete(
              requestId,
              COMMAND_COMPLETE_WAIT_MS
            );
            if (!initialCompleted) return;
            const refinedTaxonomy = await waitForTaxonomyChange(
              entityType,
              initialTaxonomy,
              TAXONOMY_CHANGE_WAIT_MS
            );
            if (refinedTaxonomy === null) return;
            try {
              // Force a fresh fetch: the cache key (by preference
              // identity) would otherwise hit the initial entry and
              // return the wrong-taxonomy response, defeating the
              // refinement's purpose.
              //
              // Keys stay from `initialTaxonomy` — the user's preference
              // identity doesn't change between the initial and refined
              // passes, only the resolved numeric does.
              const refinedResolved = await getRouteFilterResponse(
                promptForFilters,
                {
                  rolesTax: refinedTaxonomy.rolesTax,
                  skillsTax: refinedTaxonomy.skillsTax,
                  rolesKey: initialTaxonomy.rolesKey,
                  skillsKey: initialTaxonomy.skillsKey,
                },
                { bypassCache: true, filterKind: 'tdV2' }
              );
              emit({
                kind: 'applyRouteFilters',
                requestId: crypto.randomUUID(),
                ...refinedResolved,
                targetPath: path,
                entityType,
                prompt: promptForFilters,
              });
            } catch {
              // Refinement is best-effort; the initial apply has already
              // landed so the user has SOME filters applied.
            }
          })();
        })
        .catch((err) => {
          clearTimeout(clearIfStillResolving);
          const message = err instanceof Error ? err.message : 'Failed';
          mixpanel.track(
            BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_FAILED,
            {
              entity_type: entityType,
              global_assistant_route_filter_error: message,
            }
          );
          setGenerationStatus(`Couldn't resolve filters: ${message}`);
        });
    },
    [emit, setGenerationStatus, taxonomyByEntity]
  );

  const maybeAppendReportOffer = useCallback(
    (
      res: Awaited<ReturnType<typeof postGlobalAssistantSuggest>>,
      prompt: string,
      context: ShouldShowReportOfferContext
    ) => {
      if (!shouldShowReportOffer(res, context, transcript)) {
        return;
      }
      mixpanel.track(
        BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_OFFERED,
        {
          global_assistant_report_type: res.reportOffer?.reportType,
          global_assistant_report_confidence: res.reportOffer?.confidence,
        }
      );
      appendTranscript({
        id: crypto.randomUUID(),
        role: 'assistant',
        kind: 'report_offer',
        reportType: res.reportOffer?.reportType ?? '',
        reportName: res.reportOffer?.reportName ?? '',
        description: res.reportOffer?.description ?? '',
        sampleLink: res.reportOffer?.sampleLink ?? '',
        confidence: res.reportOffer?.confidence ?? 0,
        prompt,
      });
    },
    [appendTranscript, transcript]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: confirmAssistantRedirect reads customDashAssistContextRef at click time
  const handleReportOfferClick = useCallback(
    async (
      msg: Extract<GlobalAiTranscriptMessage, { kind: 'report_offer' }>
    ) => {
      const promotedTemplate = getPromotedTemplateForReportOfferClick(
        msg.reportType,
        allFlags,
        customReportsIsEnabled
      );
      const access = getGlobalAssistantReportAccess(
        msg.reportType,
        msg.reportName,
        reportBuilderConfig,
        promotedTemplate !== null
      );
      if (!access.allowed) {
        appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'error',
          text: access.message,
        });
        return;
      }

      setReportOfferLaunchingMessageId(msg.id);
      try {
        mixpanel.track(
          BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_CLICKED,
          {
            global_assistant_report_type: msg.reportType,
          }
        );

        const entityPath = getReportBuilderPath(ReportBuilderStep.ENTITY);
        const guard = confirmAssistantRedirect(entityPath);
        if (!guard.shouldContinue) {
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'text',
            text: 'Navigation canceled. I kept you on this page so your unsaved changes remain.',
          });
          return;
        }

        const promptForParams =
          msg.prompt?.trim() || lastUserPrompt?.trim() || '';
        const roleTaxonomy = taxonomyByEntity('role');

        await launchReportFromOffer({
          msg,
          promptForParams,
          roleTaxonomy,
          promotedTemplate,
          reportEntityMap: reportBuilderConfig,
          pathname: location.pathname,
          navigate,
          skipGuard: guard.skipGuard,
          onPrefillError: (text) => {
            appendTranscript({
              id: crypto.randomUUID(),
              role: 'assistant',
              kind: 'error',
              text,
            });
          },
          onPrefillWarning: (text) => {
            toast({
              title: 'Some report filters need manual selection',
              description: text,
              status: 'warning',
              duration: 7000,
              isClosable: true,
              position: 'top-right',
            });
          },
        });
        if (guard.skipGuard) {
          guard.discardChanges?.();
        }
      } finally {
        setReportOfferLaunchingMessageId(null);
      }
    },
    [
      allFlags,
      appendTranscript,
      customReportsIsEnabled,
      lastUserPrompt,
      location.pathname,
      navigate,
      reportBuilderConfig,
      taxonomyByEntity,
      toast,
    ]
  );

  const runAssistantPipeline = useCallback(
    async (
      trimmed: string,
      opts: {
        appendUserLine: boolean;
        forcedSessionScope?: GlobalAssistantEntityType;
        answers?: QuestionAnswer[];
        /**
         * When the user replies to a clarification with a short answer
         * (e.g. "company"), we still want their typed text in the
         * transcript and conversation history, but we resend the
         * ORIGINAL prompt to the backend so the router can re-route the
         * full intent (e.g. "make me a dashboard") with the now-resolved
         * scope. `displayText` is what the user typed and is shown in
         * the transcript; `trimmed` is what we send to the backend.
         */
        displayText?: string;
      }
    ) => {
      const pipelineRequestId = crypto.randomUUID();
      const addPipelineEvent = (event: string) => {
        addPipelineTimelineEvent(pipelineRequestId, event);
      };
      addPipelineEvent(opts.appendUserLine ? 'submit' : 'retry started');

      const sessionForRequest =
        opts.forcedSessionScope ?? sessionEntityScope ?? undefined;

      const userVisibleText = opts.displayText ?? trimmed;

      if (opts.appendUserLine) {
        appendTranscript({
          id: crypto.randomUUID(),
          role: 'user',
          text: userVisibleText,
        });
        // Don't overwrite lastUserPrompt when the user's typed text was
        // a continuation of an earlier prompt (we want
        // `lastUserPrompt` to keep pointing at the original intent so
        // downstream actions like "Create custom page" use the right
        // text).
        if (!opts.displayText) {
          setLastUserPrompt(trimmed);
        }
      }

      setLoading(true);

      const boundedConversation = buildBoundedConversationContext({
        transcript,
        summary: conversationSummary,
        ...(opts.appendUserLine
          ? { appendedUserTurn: { text: userVisibleText } }
          : {}),
      });
      const boundedConversationPayload =
        toConversationPayload(boundedConversation);
      // Pending clarification is computed against the transcript before
      // we appended the current user prompt, so the router can see the
      // unresolved assistant question while interpreting the new reply.
      const pendingClarification = (() => {
        const latest = findLatestClarification(transcript);
        if (!latest) {
          return undefined;
        }
        // Only structured router-meaningful clarifications (entity_scope /
        // entity_value) should be forwarded as pendingClarification. The
        // page-gen / single-chart "questions" flow is answered through the
        // `answers` field, not the router's ANSWER-vs-NEW-REQUEST decision,
        // so its untyped clarifications must not leak into the router.
        if (!latest.clarificationKind) {
          return undefined;
        }
        const text = latest.text.trim();
        if (!text) {
          return undefined;
        }
        const filteredOptions = latest.clarificationOptions
          ? latest.clarificationOptions.filter(
              (o): o is Exclude<GlobalAssistantEntityType, 'talentDiscovery'> =>
                o === 'company' || o === 'geography' || o === 'role'
            )
          : [];
        return {
          text,
          kind: latest.clarificationKind,
          ...(filteredOptions.length > 0 ? { options: filteredOptions } : {}),
        };
      })();
      const cd = customDashAssistContextRef.current;
      const requestBase = {
        prompt: trimmed,
        routeEntityHint: pathEntity,
        ...(sessionForRequest ? { sessionEntityScope: sessionForRequest } : {}),
        ...(opts.answers && opts.answers.length > 0
          ? { answers: opts.answers }
          : {}),
        context: {
          customDash: {
            isInCustomDashEditMode: cd.isInCustomDashEditMode,
            hasCurrentPageItems: cd.hasCurrentPageItems,
          },
          currentPage: buildGlobalAssistantCurrentPageContext({
            pathname: location.pathname,
            isInCustomDashEditMode: cd.isInCustomDashEditMode,
            lastAppliedFilterContext:
              lastAppliedFilterContextByPath[location.pathname],
          }),
          ...(boundedConversationPayload
            ? { conversation: boundedConversationPayload }
            : {}),
          ...(pendingClarification ? { pendingClarification } : {}),
        },
      };

      try {
        addPipelineEvent('suggest request started');
        const res = await postGlobalAssistantSuggest(requestBase);
        addPipelineEvent('suggest response received');
        addBackendTimingEvents(addPipelineEvent, res.debugTimings);
        const contextPolicy = resolveFollowOnContextPolicy(
          res.contextPolicy,
          trimmed
        );
        const followOnConversation = applyConversationPolicy({
          policy: contextPolicy,
          fullConversation: boundedConversation,
        });
        const followOnConversationPayload =
          toConversationPayload(followOnConversation);
        const followOnPendingClarification =
          contextPolicy === 'none' ? undefined : pendingClarification;
        const followOnSessionScope =
          contextPolicy === 'none' ? undefined : sessionForRequest;
        const followOnRequestBase = {
          prompt: requestBase.prompt,
          routeEntityHint: requestBase.routeEntityHint,
          ...(followOnSessionScope
            ? { sessionEntityScope: followOnSessionScope }
            : {}),
          ...(opts.answers && opts.answers.length > 0
            ? { answers: opts.answers }
            : {}),
          context: {
            customDash: requestBase.context.customDash,
            currentPage: requestBase.context.currentPage,
            ...(followOnConversationPayload
              ? { conversation: followOnConversationPayload }
              : {}),
            ...(followOnPendingClarification
              ? { pendingClarification: followOnPendingClarification }
              : {}),
          },
        };

        if (res.unableToFulfill) {
          if (opts.answers && opts.answers.length > 0) {
            appendTranscript({
              id: crypto.randomUUID(),
              role: 'assistant',
              kind: 'non_single_hint',
              text: 'I used your clarification and best judgment to proceed. Use the button below to generate a full custom page.',
              offerCustomPage: true,
              resolvedEntityType: sessionForRequest ?? pathEntity,
              prompt: trimmed,
              contextPolicy,
            });
            addPipelineEvent('unable-to-fulfill fallback: custom page hint');
            return;
          }
          mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_SUBMIT, {
            entity_type:
              res.resolvedEntityType ?? sessionForRequest ?? pathEntity,
            global_assistant_suggest_outcome: 'unable_to_fulfill',
            global_assistant_page_match_count: 0,
            global_assistant_had_clarification: false,
            global_assistant_context_policy: contextPolicy,
          });
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'text',
            text: res.unableToFulfill.reason,
          });
          addPipelineEvent('unable to fulfill appended');
          return;
        }

        const matchCount = res.existingPageMatches.length;
        // Drop matches that would render as a redundant self-link
        // suggestion to the current page (e.g. a "Compositions" chip while
        // already on `/company/compositions`). Auto-apply paths above
        // still handle the "self-link with filter prompt" case.
        const filteredExistingPageMatches = filterOutSelfLinkNavigationMatches(
          res.existingPageMatches,
          location.pathname
        );
        const selfLinkNavigationMatches = res.existingPageMatches.filter(
          (match) => isSelfLinkNavigationMatch(match, location.pathname)
        );
        const pageMatchesForTranscript =
          addCurrentPageFilterApplyOptionForDiscoveryMatch({
            matches: filteredExistingPageMatches,
            currentPathname: location.pathname,
            prompt: trimmed,
          });
        const displayableMatchCount = pageMatchesForTranscript.length;
        const hadClarification = Boolean(res.entityScopeNeedsClarification);
        const repeatedEntityScopeClarification =
          Boolean(sessionForRequest) &&
          res.clarificationKind === 'entity_scope' &&
          hadClarification;
        const effectiveHadClarification =
          hadClarification && !repeatedEntityScopeClarification;
        const suggestOutcome = (() => {
          if (matchCount > 0 && effectiveHadClarification) {
            return 'page_matches_with_clarification';
          }
          if (matchCount > 0) {
            return 'page_matches';
          }
          if (effectiveHadClarification) {
            return 'clarification_only';
          }
          if (res.offerUpdateCurrentPage) {
            return 'update_current_page_path';
          }
          if (res.offerTextChart) {
            return 'text_chart_path';
          }
          if (res.offerSingleChart) {
            return 'single_chart_path';
          }
          return 'broad_request_hint';
        })();

        mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_SUBMIT, {
          entity_type:
            res.resolvedEntityType ?? sessionForRequest ?? pathEntity,
          global_assistant_suggest_outcome: suggestOutcome,
          global_assistant_page_match_count: matchCount,
          global_assistant_had_clarification: effectiveHadClarification,
          global_assistant_context_policy: contextPolicy,
        });

        const shouldSuppressScopeRepeatMessage =
          repeatedEntityScopeClarification &&
          Boolean(
            res.assistantMessage &&
              /\b(company|geography|role)\b/i.test(res.assistantMessage)
          );
        const samePageMatch =
          !effectiveHadClarification && res.existingPageMatches.length === 1
            ? res.existingPageMatches[0]
            : undefined;
        const isSamePageDiscoveryAutoApply = Boolean(
          samePageMatch?.discoveryKind &&
            shouldAutoApplyDiscoveryMatch(samePageMatch, location.pathname)
        );
        const isSamePageDashboardAutoApply = Boolean(
          samePageMatch &&
            shouldAutoApplyDashboardFilterMatch(
              samePageMatch,
              location.pathname
            )
        );
        const isSamePageAutoApply =
          isSamePageDiscoveryAutoApply || isSamePageDashboardAutoApply;
        const shouldUseAlreadyOnPageMessage =
          !effectiveHadClarification &&
          displayableMatchCount === 0 &&
          selfLinkNavigationMatches.length > 0;
        const assistantMessageForTranscript = (() => {
          if (shouldUseAlreadyOnPageMessage) {
            return getAlreadyOnPageMessage(selfLinkNavigationMatches[0]);
          }
          if (shouldSuppressScopeRepeatMessage) {
            return isSamePageAutoApply
              ? 'Applying these filters to this page.'
              : undefined;
          }
          // When the backend is also returning an explicit clarification
          // question (entityScopeNeedsClarification), suppress the
          // separate assistantMessage bubble — the clarification text is
          // about to be appended below as the assistant's question, and
          // showing the conversational preamble too produces two
          // near-duplicate bubbles asking the same scope question.
          if (effectiveHadClarification) {
            return undefined;
          }
          if (!res.assistantMessage) {
            return isSamePageAutoApply
              ? 'Applying these filters to this page.'
              : undefined;
          }
          if (!isSamePageAutoApply) {
            return res.assistantMessage;
          }
          // Same-page discovery requests apply immediately; avoid copy that
          // implies navigation/opening.
          if (/\bopen(?:ing)?\b/i.test(res.assistantMessage)) {
            return 'Applying these filters to this page.';
          }
          return res.assistantMessage;
        })();

        if (assistantMessageForTranscript) {
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'text',
            text: assistantMessageForTranscript,
          });
          addPipelineEvent('assistant message appended');
        }

        const shouldWaitForFollowUpQuestion =
          matchCount === 0 &&
          !effectiveHadClarification &&
          !res.offerSingleChart &&
          !res.offerTextChart &&
          /[?]\s*$/.test(res.assistantMessage?.trim() ?? '');
        const reportOfferContext: ShouldShowReportOfferContext = {
          effectiveHadClarification,
          shouldWaitForFollowUpQuestion,
        };

        if (
          samePageMatch &&
          samePageMatch.discoveryKind &&
          shouldAutoApplyDiscoveryMatch(samePageMatch, location.pathname)
        ) {
          const resolvedPrompt = getDiscoveryApplyPrompt(
            samePageMatch,
            trimmed
          );
          if (resolvedPrompt) {
            const requestId = crypto.randomUUID();
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_QUEUED,
              {
                ...(samePageMatch.discoveryKind === 'talent'
                  ? {}
                  : { entity_type: samePageMatch.discoveryKind }),
                global_assistant_discovery_kind: samePageMatch.discoveryKind,
                global_assistant_route_filter_request_id: requestId,
              }
            );
            const APPLYING_STATUS = 'Applying filters…';
            setGenerationStatus(APPLYING_STATUS);
            setTimeout(() => {
              if (generationStatusRef.current === APPLYING_STATUS) {
                setGenerationStatus(null);
              }
            }, COMMAND_TTL_MS);
            emit({
              kind: 'runDiscoveryAiSearchAfterNavigate',
              requestId,
              prompt: resolvedPrompt,
              targetPath: samePageMatch.navigatePath,
              discoveryKind: samePageMatch.discoveryKind,
            });
            addPipelineEvent('same-page discovery auto-apply queued');
            maybeAppendReportOffer(res, trimmed, reportOfferContext);
            return;
          }
        }

        if (
          samePageMatch &&
          shouldAutoApplyDashboardFilterMatch(samePageMatch, location.pathname)
        ) {
          const resolvedPrompt = getDiscoveryApplyPrompt(
            samePageMatch,
            trimmed
          );
          if (resolvedPrompt) {
            queueRouteFilterApply({
              path: samePageMatch.navigatePath,
              promptForFilters: resolvedPrompt,
              entityType: inferEntityTypeFromNavigatePath(
                samePageMatch.navigatePath,
                res.resolvedEntityType ?? sessionForRequest ?? pathEntity
              ),
            });
            addPipelineEvent('same-page dashboard auto-apply queued');
            maybeAppendReportOffer(res, trimmed, reportOfferContext);
            return;
          }
        }

        if (
          displayableMatchCount > 0 &&
          !effectiveHadClarification &&
          !res.assistantMessage
        ) {
          const defaultMessage = getGlobalAssistantPageMatchesDefaultMessage({
            hasCurrentPageFilterApplyOption: hasCurrentPageFilterApplyOption(
              pageMatchesForTranscript
            ),
            matches: pageMatchesForTranscript,
          });
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'text',
            text: defaultMessage,
          });
          addPipelineEvent('default filter options message appended');
        }
        if (displayableMatchCount > 0) {
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'page_matches',
            matches: pageMatchesForTranscript,
            prompt: trimmed,
          });
          // Single-match only: pre-resolve uses the same filter kind as the
          // lone chip. Mixed-match lists can be TD + screener; warming the
          // wrong kind would be worse than a cache miss.
          if (filteredExistingPageMatches.length === 1) {
            const only = filteredExistingPageMatches[0];
            if (
              only &&
              getGlobalAssistantRouteAccess(only.navigatePath, userTabs).allowed
            ) {
              const promptForPrefetch =
                getDiscoveryApplyPrompt(only, trimmed) ?? trimmed;
              const prefetchEntity =
                res.resolvedEntityType ?? sessionForRequest ?? pathEntity;
              void prefetchRouteFilters(
                promptForPrefetch,
                taxonomyByEntity(prefetchEntity),
                {
                  filterKind: getGlobalAssistantRouteFilterKind(
                    only.navigatePath
                  ),
                  screenerEntityType: toCustomDashAssistantEntityType(
                    inferEntityTypeFromNavigatePath(
                      only.navigatePath,
                      prefetchEntity
                    )
                  ),
                }
              ).catch(() => {
                // Silent — click path will surface any real error.
              });
            }
          }
        }

        if (effectiveHadClarification) {
          // Prefer the conversational assistantMessage when it is
          // already a question (ends with '?'). The router LLM tends to
          // produce the more natural copy there ("Sure—what scope
          // should this dashboard focus on…"), while
          // entityScopeNeedsClarification is the structured fallback.
          const clarificationText = (() => {
            const am = res.assistantMessage?.trim();
            const sc = res.entityScopeNeedsClarification?.trim();
            if (am && /\?\s*$/.test(am)) {
              return am;
            }
            return sc || am || '';
          })();
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'clarification',
            text: clarificationText,
            ...(res.clarificationKind
              ? { clarificationKind: res.clarificationKind }
              : {}),
            ...(res.clarificationOptions && res.clarificationOptions.length > 0
              ? { clarificationOptions: res.clarificationOptions }
              : {}),
          });
          addPipelineEvent('clarification appended');
        }

        if (effectiveHadClarification && matchCount === 0) {
          maybeAppendReportOffer(res, trimmed, reportOfferContext);
          addPipelineEvent('pipeline ended: clarification');
          return;
        }

        if (shouldWaitForFollowUpQuestion) {
          maybeAppendReportOffer(res, trimmed, reportOfferContext);
          return;
        }

        const resolvedCustomUpdateEntity =
          res.resolvedEntityType ?? sessionForRequest ?? pathEntity;
        if (
          matchCount === 0 &&
          res.offerUpdateCurrentPage &&
          customDashAccess.allowed &&
          isCustomDashAssistantEntityType(resolvedCustomUpdateEntity) &&
          shouldAutoRunCurrentCustomPageUpdate(trimmed)
        ) {
          mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
            choice: 'custom_page_update',
            global_assistant_context_policy: contextPolicy,
          });
          emit({
            kind: 'startCustomPageStream',
            requestId: crypto.randomUUID(),
            prompt: trimmed,
            entityType: resolvedCustomUpdateEntity,
            mode: 'update',
            ...(followOnConversationPayload
              ? { conversation: followOnConversationPayload }
              : {}),
            ...(opts.answers && opts.answers.length > 0
              ? { answers: opts.answers }
              : {}),
          });
          setGenerationStatus('Updating page…');
          addPipelineEvent('clear current-page update queued');
          maybeAppendReportOffer(res, trimmed, reportOfferContext);
          return;
        }

        if (res.offerTextChart) {
          setLoading(false);
          setSingleChartLoading(true);
          const textChartRequest = {
            ...followOnRequestBase,
            ...(res.suggestedEntityScope
              ? { suggestedEntityScope: res.suggestedEntityScope }
              : {}),
          };
          try {
            addPipelineEvent('text-chart request started');
            const result = await postGlobalAssistantTextChart(textChartRequest);
            addPipelineEvent('text-chart response received');
            addBackendTimingEvents(addPipelineEvent, result.debugTimings);
            if (result.status === 'error') {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'error',
                text: result.message,
              });
              addPipelineEvent('text-chart error appended');
              return;
            }
            if (result.status === 'entity_scope') {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'clarification',
                text: result.message,
                clarificationKind: 'entity_scope',
                ...(result.clarificationOptions &&
                result.clarificationOptions.length > 0
                  ? { clarificationOptions: result.clarificationOptions }
                  : {}),
              });
              addPipelineEvent('text-chart scope clarification appended');
              return;
            }
            if (result.status === 'questions') {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'clarification',
                text: formatClarificationQuestionsAsText({
                  triggerReason: result.content.triggerReason,
                  questions: result.content.questions,
                  emptyFallback:
                    'I need a bit more detail to write that summary.',
                }),
              });
              addPipelineEvent('text-chart questions appended');
              return;
            }

            if (!isCustomDashAssistantEntityType(result.resolvedEntityType)) {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'error',
                text: 'Could not build a text summary for Individual Discovery.',
              });
              return;
            }
            const primaryFilterId =
              PRIMARY_FILTER_BY_AI_ENTITY[result.resolvedEntityType];
            const { items } = getValidatedPositionedItemsFromLlmContent(
              result.config as LLMTabContentForDispatch,
              primaryFilterId
            );
            const firstItem = items[0];
            if (!firstItem) {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'error',
                text: 'Could not build a text summary for that prompt.',
              });
              addPipelineEvent('text-chart empty config error appended');
              return;
            }

            const chartMessageId = crypto.randomUUID();
            appendTranscript({
              id: chartMessageId,
              role: 'assistant',
              kind: 'single_chart',
              intentSummary: result.intentSummary,
              item: firstItem,
              filters: result.filters as
                | TalentDiscoveryAiFilterSearchResponseV2
                | undefined,
              entityType: result.resolvedEntityType,
              prompt: trimmed,
              pipelineRequestId,
            });
            addPipelineEvent('text-chart message appended');
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_CHART_APPLIED,
              {
                entity_type: result.resolvedEntityType,
                global_assistant_context_policy: contextPolicy,
                global_assistant_text_chart: true,
              }
            );
          } catch (e) {
            appendTranscript({
              id: crypto.randomUUID(),
              role: 'assistant',
              kind: 'error',
              text: e instanceof Error ? e.message : 'Text chart failed',
            });
            addPipelineEvent('text-chart request failed');
          } finally {
            setSingleChartLoading(false);
          }
        } else if (res.offerSingleChart) {
          setLoading(false);
          setSingleChartLoading(true);
          const singleChartRequest = {
            ...followOnRequestBase,
            ...(res.suggestedEntityScope
              ? { suggestedEntityScope: res.suggestedEntityScope }
              : {}),
          };
          try {
            addPipelineEvent('single-chart request started');
            const result =
              await postGlobalAssistantSingleChart(singleChartRequest);
            addPipelineEvent('single-chart response received');
            addBackendTimingEvents(addPipelineEvent, result.debugTimings);
            if (result.status === 'error') {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'error',
                text: result.message,
              });
              addPipelineEvent('single-chart error appended');
              return;
            }
            if (result.status === 'entity_scope') {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'clarification',
                text: result.message,
                clarificationKind: 'entity_scope',
                ...(result.clarificationOptions &&
                result.clarificationOptions.length > 0
                  ? { clarificationOptions: result.clarificationOptions }
                  : {}),
              });
              addPipelineEvent('single-chart scope clarification appended');
              return;
            }
            if (result.status === 'questions') {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'clarification',
                text: formatClarificationQuestionsAsText({
                  triggerReason: result.content.triggerReason,
                  questions: result.content.questions,
                  emptyFallback:
                    'I need a bit more detail to build that chart.',
                }),
              });
              addPipelineEvent('single-chart questions appended');
              return;
            }

            if (!isCustomDashAssistantEntityType(result.resolvedEntityType)) {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'error',
                text: 'Could not build a custom chart for Individual Discovery.',
              });
              return;
            }
            const primaryFilterId =
              PRIMARY_FILTER_BY_AI_ENTITY[result.resolvedEntityType];
            const { items } = getValidatedPositionedItemsFromLlmContent(
              result.config as LLMTabContentForDispatch,
              primaryFilterId
            );
            const firstItem = items[0];
            if (!firstItem) {
              appendTranscript({
                id: crypto.randomUUID(),
                role: 'assistant',
                kind: 'error',
                text: 'Could not build a chart for that prompt.',
              });
              addPipelineEvent('single-chart empty config error appended');
              return;
            }

            const chartMessageId = crypto.randomUUID();
            appendTranscript({
              id: chartMessageId,
              role: 'assistant',
              kind: 'single_chart',
              intentSummary: result.intentSummary,
              chartSubtitle: result.chartSubtitle,
              item: firstItem,
              filters: result.filters as
                | TalentDiscoveryAiFilterSearchResponseV2
                | undefined,
              entityType: result.resolvedEntityType,
              prompt: trimmed,
              pipelineRequestId,
              standardPagePath: result.standardPagePath,
            });
            addPipelineEvent('single-chart message appended');
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_CHART_APPLIED,
              {
                entity_type: result.resolvedEntityType,
                global_assistant_context_policy: contextPolicy,
              }
            );
          } catch (e) {
            appendTranscript({
              id: crypto.randomUUID(),
              role: 'assistant',
              kind: 'error',
              text: e instanceof Error ? e.message : 'Single chart failed',
            });
            addPipelineEvent('single-chart request failed');
          } finally {
            setSingleChartLoading(false);
          }
        } else if (matchCount === 0) {
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'non_single_hint',
            text: res.offerUpdateCurrentPage
              ? 'This looks like a change to your current custom page. Update it in place, or use the same request to start a fresh dashboard.'
              : 'This looks like a broader dashboard request. Use the button below to generate a full custom page.',
            offerCustomPage: res.offerUpdateCurrentPage
              ? true
              : res.offerCustomPage,
            ...(res.offerUpdateCurrentPage
              ? { offerUpdateCurrentPage: true }
              : {}),
            resolvedEntityType:
              res.resolvedEntityType ?? sessionEntityScope ?? undefined,
            prompt: trimmed,
            contextPolicy,
          });
          addPipelineEvent('broad request hint appended');
        }

        maybeAppendReportOffer(res, trimmed, reportOfferContext);
      } catch (e) {
        appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'error',
          text: e instanceof Error ? e.message : 'Request failed',
        });
        addPipelineEvent('request failed');
      } finally {
        setLoading(false);
      }
    },
    [
      addPipelineTimelineEvent,
      sessionEntityScope,
      pathEntity,
      transcript,
      conversationSummary,
      customDashAssistContextRef,
      lastAppliedFilterContextByPath,
      customDashAccess,
      appendTranscript,
      setLastUserPrompt,
      emit,
      queueRouteFilterApply,
      location.pathname,
      setGenerationStatus,
      taxonomyByEntity,
      userTabs,
      maybeAppendReportOffer,
    ]
  );

  const handleSubmit = async () => {
    const rawPrompt = draftPrompt.trim();
    if (!rawPrompt || isReportOfferLaunching) {
      return;
    }
    const trimmed = buildPromptForSubmit({
      rawPrompt,
      lastUserPrompt,
      transcript,
    });
    setError(null);
    setDraftPrompt('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    // If the user is replying to a scope clarification with one of the
    // canonical scope words ("company" / "geography" / "role"), promote
    // that to the session scope so the next router call doesn't ask the
    // same question again, AND resend the original intent (e.g. "make
    // me a dashboard") so the router can re-route the full request
    // instead of treating the bare scope word as a fresh prompt.
    const lastClarification = findLatestClarification(transcript);
    const isPendingScopeClarification =
      lastClarification?.clarificationKind === 'entity_scope';
    const scopeReplyEntityType = isPendingScopeClarification
      ? detectScopeReplyEntityType(rawPrompt)
      : undefined;
    if (scopeReplyEntityType) {
      setSessionEntityScope(scopeReplyEntityType);
    }
    const continuationOriginalPrompt =
      scopeReplyEntityType && lastUserPrompt?.trim()
        ? lastUserPrompt.trim()
        : undefined;
    const promptForBackend = continuationOriginalPrompt ?? trimmed;
    const promptMode = lastClarification ? 'clarification_answer' : 'generate';
    trackAIAssistantPrompt({
      assistantSurface: 'global',
      conversationId: conversationTrackingIdRef.current,
      turnIndex: countUserTurns(transcript) + 1,
      promptText: promptForBackend,
      promptMode,
      entityType: pathEntity,
    });
    await runAssistantPipeline(promptForBackend, {
      appendUserLine: true,
      ...(scopeReplyEntityType
        ? { forcedSessionScope: scopeReplyEntityType }
        : {}),
      ...(continuationOriginalPrompt ? { displayText: trimmed } : {}),
    });
  };

  const navigateToMatch = (
    match: GlobalAssistantSuggestMatch,
    options?: { standard?: boolean; prompt?: string }
  ) => {
    const path = match.navigatePath;
    // Prefer the prompt that produced THIS match (attached to the
    // page_matches message) so clicking a chip from an earlier prompt — or
    // a hydrated session — resolves filters for the correct prompt. Falls
    // back to `lastUserPrompt` for older persisted messages that don't
    // carry `prompt`.
    const promptForFilters = getDiscoveryApplyPrompt(
      match,
      options?.prompt?.trim() || lastUserPrompt?.trim() || undefined
    );
    const action = getGlobalAssistantPageMatchAction(match, promptForFilters);
    const entityType = inferEntityTypeFromNavigatePath(
      path,
      sessionEntityScope ?? pathEntity
    );
    const requestId = crypto.randomUUID();
    const isAlreadyOnTargetPage = globalAssistantPathsMatch(
      location.pathname,
      path
    );
    const guard = confirmAssistantRedirect(path);
    if (!guard.shouldContinue) {
      appendTranscript({
        id: crypto.randomUUID(),
        role: 'assistant',
        kind: 'text',
        text: 'Navigation canceled. I kept you on this page so your unsaved changes remain.',
      });
      return;
    }

    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: options?.standard ? 'standard_template_page' : 'existing_page',
      navigate_path: path,
    });

    if (action.kind === 'apply_discovery_filters_after_navigate') {
      mixpanel.track(
        BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_QUEUED,
        {
          ...(action.discoveryKind === 'talent'
            ? {}
            : { entity_type: action.discoveryKind }),
          global_assistant_discovery_kind: action.discoveryKind,
          global_assistant_route_filter_request_id: requestId,
        }
      );
      const STATUS_MESSAGE = isAlreadyOnTargetPage
        ? 'Applying filters…'
        : 'Opening Discovery…';
      setGenerationStatus(STATUS_MESSAGE);
      setTimeout(() => {
        if (generationStatusRef.current === STATUS_MESSAGE) {
          setGenerationStatus(null);
        }
      }, COMMAND_TTL_MS);
      emit({
        kind: 'runDiscoveryAiSearchAfterNavigate',
        requestId,
        prompt: promptForFilters ?? '',
        targetPath: path,
        discoveryKind: action.discoveryKind,
      });
      if (!isAlreadyOnTargetPage) {
        navigate(
          path,
          guard.skipGuard ? { state: { skipGuard: true } } : undefined
        );
      }
      guard.discardChanges?.();
      return;
    }

    if (action.kind === 'resolve_route_filters' && promptForFilters) {
      queueRouteFilterApply({
        path,
        promptForFilters,
        entityType,
        requestId,
      });
      if (isAlreadyOnTargetPage) {
        guard.discardChanges?.();
        return;
      }
    }

    navigate(
      path,
      guard.skipGuard ? { state: { skipGuard: true } } : undefined
    );
    guard.discardChanges?.();
  };

  const startCustomPageCreate = (
    entityType: CustomDashAssistantEntityType,
    options?: { prompt?: string; contextPolicy?: GlobalAssistantContextPolicy }
  ) => {
    if (!customDashAccess.allowed) {
      setError(customDashAccess.message);
      return;
    }
    // Prefer the prompt attached to the message that triggered the click
    // (so the "Create custom page" button still works after session
    // hydration, where `lastUserPrompt` is not restored from localStorage).
    const text = options?.prompt?.trim() || lastUserPrompt?.trim() || undefined;
    if (!text) {
      setError('Run a prompt first so we know what to build.');
      return;
    }
    const guard = confirmAssistantRedirect();
    if (!guard.shouldContinue) {
      return;
    }
    const targetPath = createPageDraft(entityType, guard.skipGuard);
    if (!targetPath) {
      return;
    }
    const targetPathname = normalizeAndNavigateToPath(
      targetPath,
      navigate,
      guard.skipGuard
    );
    const isSamePath = globalAssistantPathsMatch(
      location.pathname,
      targetPathname
    );
    if (!isSamePath) {
      guard.discardChanges?.();
    }
    const contextPolicy = resolveFollowOnContextPolicy(
      options?.contextPolicy,
      text
    );
    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: 'custom_page',
      global_assistant_context_policy: contextPolicy,
    });
    const fullConversationContext = buildBoundedConversationContext({
      transcript,
      summary: conversationSummary,
    });
    const conversationPayload = toConversationPayload(
      applyConversationPolicy({
        policy: contextPolicy,
        fullConversation: fullConversationContext,
      })
    );
    emit({
      kind: 'startCustomPageStream',
      requestId: crypto.randomUUID(),
      prompt: text,
      entityType,
      mode: 'create',
      targetPath: targetPathname,
      ...(conversationPayload ? { conversation: conversationPayload } : {}),
    });
    setGenerationStatus('Opening custom page…');
  };

  const startCustomPageUpdate = (
    entityType: CustomDashAssistantEntityType,
    options?: { prompt?: string; contextPolicy?: GlobalAssistantContextPolicy }
  ) => {
    if (!customDashAccess.allowed) {
      setError(customDashAccess.message);
      return;
    }
    const text = options?.prompt?.trim() || lastUserPrompt?.trim();
    if (!text) {
      setError('Run a prompt first so we know what to build.');
      return;
    }
    const contextPolicy = resolveFollowOnContextPolicy(
      options?.contextPolicy,
      text
    );
    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: 'custom_page_update',
      global_assistant_context_policy: contextPolicy,
    });
    const fullConversationContext = buildBoundedConversationContext({
      transcript,
      summary: conversationSummary,
    });
    const conversationPayload = toConversationPayload(
      applyConversationPolicy({
        policy: contextPolicy,
        fullConversation: fullConversationContext,
      })
    );
    // Route through the command queue like other custom-page operations so
    // the bridge can pick up the current page's live tab content at handle
    // time. Update mode is deliberately not cached (the current page IS the
    // state being updated).
    const requestId = crypto.randomUUID();
    emit({
      kind: 'startCustomPageStream',
      requestId,
      prompt: text,
      entityType,
      mode: 'update',
      ...(conversationPayload ? { conversation: conversationPayload } : {}),
    });
    setGenerationStatus('Updating page…');
  };

  const startSingleChartAsCustomPage = (
    msg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => {
    if (!customDashAccess.allowed) {
      setError(customDashAccess.message);
      return;
    }
    if (!isCustomDashAssistantEntityType(msg.entityType)) {
      setError('Custom Dash is not available for Individual Discovery.');
      return;
    }
    const guard = confirmAssistantRedirect();
    if (!guard.shouldContinue) {
      return;
    }
    const targetPath = createPageDraft(msg.entityType, guard.skipGuard);
    if (!targetPath) {
      return;
    }
    const targetPathname = normalizeAndNavigateToPath(
      targetPath,
      navigate,
      guard.skipGuard
    );
    guard.discardChanges?.();
    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: 'single_chart_open_custom_draft',
      entity_type: msg.entityType,
    });
    setGenerationStatus('Opening custom page…');
    emit({
      kind: 'applySingleChart',
      requestId: crypto.randomUUID(),
      entityType: msg.entityType,
      config: buildLlmTabContentFromPositionedItemForGlobalAssistant(msg.item),
      filters: msg.filters,
      prompt: msg.prompt?.trim() || lastUserPrompt?.trim() || undefined,
      targetPath: targetPathname,
    });
  };

  const applySingleChartFiltersToCurrentPage = (
    msg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => {
    setError(null);
    const currentPath = location.pathname;
    const isCurrentPageFilterCapable =
      isGlobalAssistantFilterApplyCapablePath(currentPath) ||
      customDashAssistContextRef.current.isInCustomDashEditMode;
    if (!isCurrentPageFilterCapable) {
      setError('Filters cannot be applied on this page.');
      return;
    }
    const promptForFilters =
      msg.prompt?.trim() || lastUserPrompt?.trim() || undefined;
    if (!promptForFilters) {
      setError('Run a prompt first so we know which filters to apply.');
      return;
    }
    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: 'single_chart_apply_filters_current_page',
      navigate_path: currentPath,
    });
    appendTranscript({
      id: crypto.randomUUID(),
      role: 'assistant',
      kind: 'text',
      text: 'Applying these filters to this page.',
    });
    queueRouteFilterApply({
      path: currentPath,
      promptForFilters,
      entityType: inferEntityTypeFromNavigatePath(
        currentPath,
        sessionEntityScope ?? pathEntity
      ),
    });
  };

  const handlePageMatchClick = (
    match: GlobalAssistantSuggestMatch,
    prompt: string | undefined
  ) => {
    const matchedEntityType = inferEntityTypeFromNavigatePath(
      match.navigatePath,
      sessionEntityScope ?? pathEntity
    );
    const shouldUpdateCurrentCustomPage =
      match.pageId.startsWith('__current__:custom-dash-filter:') &&
      globalAssistantPathsMatch(location.pathname, match.navigatePath) &&
      customDashAssistContextRef.current.isInCustomDashEditMode &&
      isCustomDashAssistantEntityType(matchedEntityType);

    if (shouldUpdateCurrentCustomPage) {
      startCustomPageUpdate(matchedEntityType, { prompt });
      return;
    }

    navigateToMatch(match, {
      standard: match.pageId.startsWith('__std__:'),
      prompt,
    });
  };

  const renderMessage = (
    msg: GlobalAiTranscriptMessage,
    previousMsg: GlobalAiTranscriptMessage | undefined
  ) => {
    const showAvatar = !(previousMsg && messageRendersAvatar(previousMsg));
    if (msg.role === 'user') {
      return (
        <Box key={msg.id} alignSelf="flex-end" maxW="95%" mb={3}>
          <Box
            bg="gray.50"
            borderRadius="md"
            px={2.5}
            py={1.5}
            borderWidth="1px"
            borderColor="gray.200"
          >
            <Text fontSize="12px" lineHeight="15px" color={BRAND_NAVY}>
              {msg.text}
            </Text>
          </Box>
        </Box>
      );
    }

    if (msg.kind === 'text') {
      return (
        <Flex
          key={msg.id}
          alignSelf="stretch"
          align="flex-start"
          gap={2}
          mb={3}
          maxW="100%"
        >
          <AssistantAvatarSlot show={showAvatar} />
          <Text
            fontSize="12px"
            lineHeight="15px"
            color={BRAND_NAVY}
            flex={1}
            minW={0}
          >
            {msg.text}
          </Text>
        </Flex>
      );
    }

    if (msg.kind === 'clarification') {
      return (
        <Flex
          key={msg.id}
          alignSelf="stretch"
          align="flex-start"
          gap={2}
          mb={3}
          maxW="100%"
        >
          <AssistantAvatarSlot show={showAvatar} />
          <Text
            fontSize="12px"
            lineHeight="15px"
            color={BRAND_NAVY}
            flex={1}
            minW={0}
          >
            {msg.text}
          </Text>
        </Flex>
      );
    }

    if (msg.kind === 'error') {
      return (
        <Box key={msg.id} alignSelf="stretch" maxW="100%" mb={3} w="100%">
          <Alert status="error" borderRadius="md" fontSize="xs" py={2}>
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold" fontSize="11px">
                Something went wrong
              </Text>
              <Text fontSize="11px">{msg.text}</Text>
            </Box>
          </Alert>
        </Box>
      );
    }

    if (msg.kind === 'page_matches') {
      return (
        <Flex
          key={msg.id}
          direction="column"
          align="flex-end"
          gap={2}
          alignSelf="stretch"
          maxW="100%"
          mb={3}
          w="100%"
        >
          {msg.matches.map((m) => {
            const access = getGlobalAssistantRouteAccess(
              m.navigatePath,
              userTabs
            );
            if (!access.allowed) {
              return (
                <GlobalAssistantPermissionUpsell
                  key={m.pageId}
                  access={access}
                  data-testid={`global-ai-page-match-no-access-${m.pageId}`}
                />
              );
            }
            const isFilterApplyChip = isCurrentPageFilterApplyMatch(m);
            const chipLabel = isFilterApplyChip ? m.name : `Open ${m.name}`;
            return (
              <Box key={m.pageId} maxW="100%">
                <Button
                  {...CHIP_BUTTON_PROPS}
                  maxW="100%"
                  justifyContent="center"
                  rightIcon={
                    isFilterApplyChip ? undefined : (
                      <ExternalLinkIcon boxSize="10px" />
                    )
                  }
                  data-testid={`global-ai-page-match-${m.pageId}`}
                  onClick={() => handlePageMatchClick(m, msg.prompt)}
                >
                  {chipLabel}
                </Button>
              </Box>
            );
          })}
        </Flex>
      );
    }

    if (msg.kind === 'report_offer') {
      const promotedTemplate = getPromotedTemplateForReportOfferClick(
        msg.reportType,
        allFlags,
        customReportsIsEnabled
      );
      const access = getGlobalAssistantReportAccess(
        msg.reportType,
        msg.reportName,
        reportBuilderConfig,
        promotedTemplate !== null
      );
      const isLaunching = reportOfferLaunchingMessageId === msg.id;
      const samplePath = `/${PagePaths.REPORTS_SAMPLE}?link=${encodeURIComponent(msg.sampleLink)}`;
      const reportOfferExplanation = (() => {
        if (msg.description) {
          return `This looks like a good fit for our ${msg.reportName} report — ${msg.description}`;
        }
        return `This looks like a good fit for our ${msg.reportName} report.`;
      })();
      return (
        <Flex
          key={msg.id}
          direction="column"
          align="flex-end"
          gap={2}
          alignSelf="stretch"
          maxW="100%"
          mb={3}
          w="100%"
        >
          <Text
            fontSize="12px"
            lineHeight="15px"
            color={BRAND_NAVY}
            w="100%"
            alignSelf="stretch"
          >
            {reportOfferExplanation}
          </Text>
          {!access.allowed ? (
            <GlobalAssistantPermissionUpsell
              access={{
                allowed: false,
                featureLabel: msg.reportName,
                message: access.message,
              }}
              data-testid={`global-ai-report-offer-no-access-${msg.reportType}`}
            />
          ) : (
            <Box maxW="100%">
              <Button
                {...CHIP_BUTTON_PROPS}
                maxW="100%"
                data-testid={`global-ai-report-offer-${msg.reportType}`}
                isLoading={isLaunching}
                loadingText="Preparing report…"
                isDisabled={
                  loading ||
                  singleChartLoading ||
                  isCommandInFlight ||
                  isReportOfferLaunching
                }
                onClick={() => {
                  void handleReportOfferClick(msg);
                }}
              >
                {`Generate ${msg.reportName} Report`}
              </Button>
              {isLaunching ? (
                <Flex align="center" gap={2} mt={2}>
                  <Spinner size="xs" color={BRAND_NAVY} />
                  <Text fontSize="12px" color={`${BRAND_NAVY}80`}>
                    Extracting filters and opening report builder…
                  </Text>
                </Flex>
              ) : null}
            </Box>
          )}
          <Link
            href={samplePath}
            fontSize="12px"
            lineHeight="15px"
            color="brightGreen.600"
            fontWeight="700"
            data-testid={`global-ai-report-sample-${msg.reportType}`}
            pointerEvents={isReportOfferLaunching ? 'none' : undefined}
            opacity={isReportOfferLaunching ? 0.5 : 1}
            onClick={(event) => {
              if (isReportOfferLaunching) {
                event.preventDefault();
                return;
              }
              event.preventDefault();
              const guard = confirmAssistantRedirect(samplePath);
              if (!guard.shouldContinue) {
                appendTranscript({
                  id: crypto.randomUUID(),
                  role: 'assistant',
                  kind: 'text',
                  text: 'Navigation canceled. I kept you on this page so your unsaved changes remain.',
                });
                return;
              }
              if (guard.skipGuard) {
                guard.discardChanges?.();
              }
              navigate(
                samplePath,
                guard.skipGuard
                  ? { replace: true, state: { skipGuard: true } }
                  : { replace: true }
              );
            }}
          >
            View sample report
          </Link>
        </Flex>
      );
    }

    if (msg.kind === 'non_single_hint') {
      const canUseCustomDashEntity = isCustomDashAssistantEntityType(
        msg.resolvedEntityType
      );
      const customDashActionAccess: GlobalAssistantAccessDecision =
        customDashAccess.allowed && !canUseCustomDashEntity
          ? {
              allowed: false,
              featureLabel: 'Custom Dash',
              message: 'Custom Dash is not available for Individual Discovery.',
            }
          : customDashAccess;
      const customDashActionDisabled = !customDashActionAccess.allowed;
      const wantsCustomDashAction =
        Boolean(msg.offerUpdateCurrentPage && msg.resolvedEntityType) ||
        Boolean(msg.offerCustomPage && msg.resolvedEntityType);
      return (
        <Flex
          key={msg.id}
          direction="column"
          align="flex-end"
          gap={2}
          alignSelf="stretch"
          maxW="100%"
          mb={3}
          w="100%"
        >
          <Text fontSize="12px" lineHeight="15px" color={BRAND_NAVY} w="100%">
            {msg.text}
          </Text>
          {customDashActionDisabled && wantsCustomDashAction ? (
            <GlobalAssistantPermissionUpsell
              access={customDashActionAccess}
              data-testid="global-ai-non-single-custom-dash-upsell"
            />
          ) : null}
          {msg.offerUpdateCurrentPage &&
          msg.resolvedEntityType &&
          !customDashActionDisabled ? (
            <Box maxW="100%">
              <Button
                {...CHIP_BUTTON_PROPS}
                maxW="100%"
                data-testid="global-ai-update-current-page"
                isDisabled={loading || singleChartLoading || isCommandInFlight}
                onClick={() => {
                  const et = msg.resolvedEntityType;
                  if (isCustomDashAssistantEntityType(et)) {
                    startCustomPageUpdate(et, {
                      prompt: msg.prompt,
                      contextPolicy: msg.contextPolicy,
                    });
                  }
                }}
              >
                Update current page
              </Button>
            </Box>
          ) : null}
          {msg.offerCustomPage &&
          msg.resolvedEntityType &&
          !customDashActionDisabled ? (
            <Box maxW="100%">
              <Button
                {...CHIP_BUTTON_PROPS}
                maxW="100%"
                data-testid="global-ai-create-custom-page"
                isDisabled={loading || singleChartLoading || isCommandInFlight}
                onClick={() => {
                  const et = msg.resolvedEntityType;
                  if (isCustomDashAssistantEntityType(et)) {
                    startCustomPageCreate(et, {
                      prompt: msg.prompt,
                      contextPolicy: msg.contextPolicy,
                    });
                  }
                }}
              >
                {msg.offerUpdateCurrentPage
                  ? 'Create new dashboard from this request'
                  : 'Create custom page for this request'}
              </Button>
            </Box>
          ) : null}
        </Flex>
      );
    }

    if (msg.kind === 'single_chart') {
      const isTextChart = msg.item.plot?.chartType === 'text';
      const canOpenAsCustomPage = isCustomDashAssistantEntityType(
        msg.entityType
      );
      const canApplyFiltersToCurrentPage =
        !isTextChart &&
        (isGlobalAssistantFilterApplyCapablePath(location.pathname) ||
          customDashAssistContextRef.current.isInCustomDashEditMode);
      const hasPromptForCurrentPageApply = Boolean(
        msg.prompt?.trim() || lastUserPrompt?.trim()
      );
      const customDashActionAccess: GlobalAssistantAccessDecision =
        customDashAccess.allowed && !canOpenAsCustomPage
          ? {
              allowed: false,
              featureLabel: 'Custom Dash',
              message: 'Custom Dash is not available for Individual Discovery.',
            }
          : customDashAccess;
      const customDashActionDisabled = !customDashActionAccess.allowed;

      if (isTextChart) {
        return (
          <Box
            key={msg.id}
            alignSelf="stretch"
            maxW="100%"
            mb={3}
            w="100%"
            flexShrink={0}
          >
            <Flex align="flex-start" gap={2} w="100%">
              <AssistantAvatarSlot show={showAvatar} />
              <Box flex={1} minW={0} w="100%">
                <GlobalAIAssistantChartPreviewSlot
                  messageId={msg.id}
                  intersectionRoot={transcriptScrollRoot}
                  variant="text"
                />
              </Box>
            </Flex>
            <Flex direction="column" align="flex-end" gap={2} mt={2} w="100%">
              {customDashActionDisabled ? (
                <GlobalAssistantPermissionUpsell
                  access={customDashActionAccess}
                  data-testid="global-ai-single-chart-custom-dash-upsell"
                />
              ) : (
                <Button
                  {...CHIP_BUTTON_PROPS}
                  data-testid="global-ai-open-single-chart-custom-page"
                  maxW="100%"
                  isDisabled={
                    loading || singleChartLoading || isCommandInFlight
                  }
                  onClick={() => startSingleChartAsCustomPage(msg)}
                >
                  Open as custom page
                </Button>
              )}
            </Flex>
          </Box>
        );
      }

      return (
        <Box
          key={msg.id}
          alignSelf="stretch"
          maxW="100%"
          mb={3}
          w="100%"
          flexShrink={0}
        >
          {msg.intentSummary ? (
            <Flex align="flex-start" gap={2} mb={2}>
              <AssistantAvatarSlot show={showAvatar} />
              <Text
                fontSize="12px"
                lineHeight="15px"
                color={BRAND_NAVY}
                flex={1}
                minW={0}
              >
                {msg.intentSummary}
              </Text>
            </Flex>
          ) : null}
          <GlobalAIAssistantChartPreviewSlot
            messageId={msg.id}
            intersectionRoot={transcriptScrollRoot}
          />
          <Flex direction="column" align="flex-end" gap={2} mt={2} w="100%">
            {customDashActionDisabled ? (
              <GlobalAssistantPermissionUpsell
                access={customDashActionAccess}
                data-testid="global-ai-single-chart-custom-dash-upsell"
              />
            ) : (
              <Button
                {...CHIP_BUTTON_PROPS}
                data-testid="global-ai-open-single-chart-custom-page"
                maxW="100%"
                isDisabled={loading || singleChartLoading || isCommandInFlight}
                onClick={() => startSingleChartAsCustomPage(msg)}
              >
                Open as custom page
              </Button>
            )}
            {canApplyFiltersToCurrentPage ? (
              <Button
                {...CHIP_BUTTON_PROPS}
                data-testid="global-ai-apply-single-chart-current-page"
                maxW="100%"
                isDisabled={
                  !hasPromptForCurrentPageApply ||
                  loading ||
                  singleChartLoading ||
                  isCommandInFlight
                }
                onClick={() => applySingleChartFiltersToCurrentPage(msg)}
              >
                Apply filters to current page
              </Button>
            ) : null}
            {msg.standardPagePath
              ? (() => {
                  const standardMatch = getSingleChartStandardPageMatch(msg);
                  if (!standardMatch) {
                    return null;
                  }
                  const access = getGlobalAssistantRouteAccess(
                    standardMatch.navigatePath,
                    userTabs
                  );
                  return (
                    <Box maxW="100%">
                      <Button
                        {...CHIP_BUTTON_PROPS}
                        data-testid="global-ai-open-standard-page"
                        maxW="100%"
                        onClick={
                          access.allowed
                            ? () => {
                                navigateToMatch(standardMatch, {
                                  standard: true,
                                  prompt: msg.prompt,
                                });
                              }
                            : undefined
                        }
                        isDisabled={
                          !access.allowed ||
                          loading ||
                          singleChartLoading ||
                          isCommandInFlight
                        }
                      >
                        {access.allowed
                          ? getSingleChartStandardPageButtonLabel(
                              msg.standardPagePath
                            )
                          : `No access to ${access.featureLabel}`}
                      </Button>
                      {access.allowed ? null : (
                        <GlobalAssistantPermissionUpsell
                          access={access}
                          data-testid="global-ai-single-chart-standard-page-upsell"
                        />
                      )}
                    </Box>
                  );
                })()
              : null}
          </Flex>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      position="fixed"
      right={GLOBAL_AI_OVERLAY_RIGHT}
      bottom={GLOBAL_AI_OVERLAY_BOTTOM}
      width={GLOBAL_AI_OVERLAY_WIDTH}
      maxW="calc(100vw - 48px)"
      h={GLOBAL_AI_OVERLAY_HEIGHT}
      maxH={GLOBAL_AI_OVERLAY_HEIGHT}
      bg="white"
      boxShadow="0px 4px 21.1px rgba(0, 0, 0, 0.25)"
      borderRadius="8px"
      zIndex={GLOBAL_AI_OVERLAY_Z_INDEX}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      pointerEvents={isDrawerOpen ? 'auto' : 'none'}
      opacity={isDrawerOpen ? 1 : 0}
      transform={
        isDrawerOpen ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.98)'
      }
      transition="opacity 0.2s ease-out, transform 0.2s ease-out"
      sx={{ fontFamily: OVERLAY_FONT_STACK }}
      aria-hidden={isDrawerOpen ? undefined : true}
    >
      <Flex
        align="center"
        justify="space-between"
        h="36px"
        px={3}
        flexShrink={0}
        bg="white"
        borderBottom="1px solid"
        borderColor="#D9D9D9"
        borderTopRadius="8px"
      >
        <IconButton
          aria-label="Clear session"
          icon={<EditIcon boxSize={4} />}
          size="xs"
          variant="ghost"
          color={BRAND_NAVY}
          onClick={handleNewSession}
          isDisabled={loading || singleChartLoading || isReportOfferLaunching}
          w={ASSISTANT_ICON_SIZE}
          h={ASSISTANT_ICON_SIZE}
          minW={ASSISTANT_ICON_SIZE}
          p={0}
          _hover={{ bg: 'blackAlpha.50' }}
          _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
        />
        <Heading
          size="sm"
          color={BRAND_NAVY}
          fontWeight="600"
          fontSize="16px"
          lineHeight="20px"
          noOfLines={1}
          flex={1}
          textAlign="left"
          px={2}
        >
          AI Chat
        </Heading>
        <IconButton
          aria-label="Close AI Assistant"
          icon={<CloseIcon boxSize={3} />}
          size="sm"
          variant="ghost"
          color={BRAND_NAVY}
          onClick={onCloseAndStopRecording}
          _hover={{ bg: 'blackAlpha.50' }}
        />
      </Flex>

      <Flex
        direction="column"
        flex={1}
        minH={0}
        overflow="hidden"
        px={3}
        pt={2}
      >
        <Box
          ref={handleTranscriptScrollRootRef}
          flex={1}
          minH={0}
          w="100%"
          overflowY="auto"
          overflowX="hidden"
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          data-testid="global-ai-assistant-transcript"
          pb={6}
          pr={2}
          sx={{
            '& > *': {
              flexShrink: 0,
            },
          }}
        >
          {transcript.length === 0 && !loading && !singleChartLoading ? (
            <Flex align="flex-start" gap={2}>
              <AssistantAvatar />
              <Text fontSize="12px" lineHeight="15px" color={`${BRAND_NAVY}80`}>
                Ask for dashboards, filters, or a quick chart…
              </Text>
            </Flex>
          ) : null}
          {transcript.map((msg, index) =>
            renderMessage(msg, index > 0 ? transcript[index - 1] : undefined)
          )}
          {generationStatus ? (
            <Flex align="center" gap={2} py={2} justify="center" px={1}>
              <Spinner size="sm" color={BRAND_NAVY} />
              <Text
                fontSize="12px"
                color={BRAND_NAVY}
                opacity={0.9}
                textAlign="center"
                data-testid="global-ai-assistant-generation-status"
              >
                {generationStatus}
              </Text>
            </Flex>
          ) : null}
          {(loading || singleChartLoading) && (
            <Flex align="center" gap={2} py={3} justify="center">
              <Spinner size="sm" color={BRAND_NAVY} />
              <Text fontSize="12px" color={BRAND_NAVY} opacity={0.8}>
                Thinking…
              </Text>
            </Flex>
          )}
          {error && (
            <Alert status="error" borderRadius="md" fontSize="xs" mb={3} py={2}>
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold" fontSize="11px">
                  Something went wrong
                </Text>
                <Text fontSize="11px">{error}</Text>
              </Box>
            </Alert>
          )}
        </Box>

        <Box flexShrink={0} pt={2} pb={3}>
          <Box
            boxSizing="border-box"
            bg="#F1F5F9"
            borderRadius="12px"
            border="1px solid"
            borderColor="#E2E8F0"
            position="relative"
            display="flex"
            alignItems="flex-end"
            gap={1}
            px={2}
            py={1}
            minH="35px"
          >
            <Textarea
              ref={textareaRef}
              data-testid="global-ai-assistant-prompt"
              placeholder="Ask AI..."
              value={draftPrompt}
              onChange={handlePromptChange}
              fontSize="14px"
              lineHeight="18px"
              color={BRAND_NAVY}
              border="none"
              bg="transparent"
              resize="none"
              overflowY="auto"
              p={2}
              pr={1}
              rows={1}
              maxH="120px"
              flex={1}
              minH="20px"
              _placeholder={{ color: BRAND_NAVY, opacity: 0.5 }}
              style={{
                height: 'auto',
                minHeight: '20px',
              }}
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              isDisabled={
                loading || singleChartLoading || isReportOfferLaunching
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSubmit();
                }
                if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  textareaRef.current?.select();
                }
              }}
            />
            <Flex align="center" gap={0} flexShrink={0} pb="2px">
              <SpeechInput
                ref={speechInputRef}
                onTranscriptComplete={(transcriptText) => {
                  setDraftPrompt((prev) =>
                    prev ? `${prev} ${transcriptText}` : transcriptText
                  );
                }}
                onEnter={() => void handleSubmit()}
                onStartListening={noop}
                textAreaRef={textareaRef}
                idleIconColor="#2D426A80"
              />
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}
